import React from "react";
import { splitFormControlWrapperProps } from "@cmp-src/form/FormControlWrapper/helpers";
import { FormControlWrapper } from "@cmp-src/form/FormControlWrapper";
import type {
  CalendarInputProps,
  CalendarInputType
} from "./types";
import { Calendar } from "@cmp-src/calendar/Calendar";
import { CalendarTrigger } from "@cmp-src/components/calendar/CalendarTrigger";
import { Calendar2Icon } from "@cmp-src/icons/icons";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { ModalButtons } from "@cmp-src/overlay/ModalButtons/ModalButtons";
import { CalendarDatesList } from "@cmp-src/calendar/CalendarDatesList";
import type { CalendarProps, DateValue } from "@cmp-src/calendar/types";
import { cn, useDisclosure } from "@gettactic/helpers";
import { Switch } from "@cmp-src/primitives/Switch";
import { ArrowsGroup } from "@cmp-src/components/ArrowsGroup/ArrowsGroup";
import {
  getNextDay,
  getPreviousDay
} from "@cmp-src/calendar/CalendarDayView/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import {
  asDateRange,
  resolveRangeSelection
} from "@cmp-src/calendar/rangeSelection";

export const CalendarInputInput = React.forwardRef<
  HTMLInputElement,
  Omit<CalendarInputProps, "mode"> &
    Partial<Pick<CalendarInputProps, "mode">> & {
      onSelect?: (date: DateValue | undefined) => void;
    } & {
      withArrows?: boolean;
      showMultipleLabel?: boolean;
      hideSideContent?: boolean;
      hideCancel?: boolean;
      hideMultipleText?: boolean;
    }
>((props, ref) => {
  const {
    inputClassName,
    className,
    mode: selectedMode = "single",
    allowMultipleSwitch = false,
    selectedDate,
    setValueOnApply = false,
    onSelect,
    toYear: controlledToYear,
    withArrows = false,
    showDaysCount = false,
    showMultipleLabel = false,
    showDayName = true,
    hideSideContent = false,
    hideCancel = false,
    hideMultipleText = false,
    variant = "default",
    restartRangeOnComplete = false,
    ...rest
  } = props;

  const toYear = controlledToYear ?? new Date().getFullYear() + 5;

  const pickerDisclosure = useDisclosure();
  const { isBreakpoint } = useBreakpoint();
  const [selectedDates, setSelectedDates] = React.useState<
    DateValue | undefined
  >(selectedDate);
  const [forceMultiDay, setForceMultiDay] = React.useState<boolean>(
    selectedMode === "multiple" || (Array.isArray(selectedDate) && selectedDate.length > 1)
  );
  const isMd = isBreakpoint("md");

  const deleteDate = (dateIndex: number) => {
    if (Array.isArray(selectedDates))
      setSelectedDates(selectedDates.filter((_, index) => index !== dateIndex));
  };

  // DayPicker passes the day that was clicked alongside the range it computed
  // from it. Only the range is forwarded on, but the day is what tells us a
  // click landed on an already-finished range — see `resolveRangeSelection`.
  const handleSelectDate = (
    date: DateValue | undefined,
    triggerDate?: Date
  ) => {
    const selection =
      restartRangeOnComplete && mode === "range"
        ? resolveRangeSelection(
            asDateRange(date),
            triggerDate,
            asDateRange(value)
          )
        : date;

    if (!setValueOnApply) {
      onSelect?.(selection);
      if (mode === "single") {
        pickerDisclosure.close();
      }
      return;
    }
    setSelectedDates(selection);
  };

  const handleOnApply = () => {
    onSelect?.(selectedDates);
    pickerDisclosure.close();
  };

  const handleCancel = () => {
    pickerDisclosure.close();
  };

  const handleMultiDaySwitch = (checked: boolean) => {
    setForceMultiDay(checked);

    // Make sure to keep at least one date selected when switching from multi-day to single-day mode
    if (checked && selectedMode === "single") {
      if (selectedDate instanceof Date) handleSelectDate([selectedDate]);
      return;
    }

    if (!checked && selectedMode === "single") {
      if (Array.isArray(selectedDate) && selectedDate[0])
        handleSelectDate(selectedDate[0]);
      return;
    }
  };

  const properties: Omit<CalendarProps, "mode"> & {
    onSelect: typeof handleSelectDate;
  } = {
    ...rest,
    toYear,
    required: false,
    className: cn("border-0", className),
    onSelect: handleSelectDate
  };

  const value = setValueOnApply ? selectedDates : selectedDate;
  const showMultiDaySwitch =
    allowMultipleSwitch /*&& selectedMode !== 'multiple'*/;
  const mode = forceMultiDay
    ? "multiple"
    : selectedMode === "multiple"
      ? "single"
      : selectedMode;

  let calendar = <></>;

  React.useEffect(() => {
    if (pickerDisclosure.isOpen) {
      setSelectedDates(selectedDate);
      // Auto-toggle to multi-day mode only if MORE than 1 date is selected
      if (Array.isArray(selectedDate) && selectedDate.length > 1) {
        setForceMultiDay(true);
      } else if (selectedMode === "single") {
        // If only 1 date or less, ensure toggle is OFF (unless mode is explicitly "multiple")
        setForceMultiDay(false);
      }
    }
  }, [pickerDisclosure.isOpen, selectedDate, selectedMode]);

  if (mode === "single") {
    calendar = (
      <Calendar
        mode={"single"}
        selected={value instanceof Date ? value : undefined}
        {...properties}
      />
    );
  }

  if (mode === "range") {
    calendar = (
      <Calendar
        mode={"range"}
        selected={
          value && !Array.isArray(value) && !(value instanceof Date)
            ? value
            : undefined
        }
        {...properties}
      />
    );
  }

  if (mode === "multiple") {
    calendar = (
      <Calendar
        mode={"multiple"}
        selected={Array.isArray(value) ? value : undefined}
        {...properties}
      />
    );
  }

  const handleNext =
    selectedDate instanceof Date
      ? () => {
          const tomorrow = getNextDay(selectedDate);
          onSelect?.(tomorrow);
        }
      : undefined;

  const handlePrev =
    selectedDate instanceof Date
      ? () => {
          const yesterday = getPreviousDay(selectedDate);
          onSelect?.(yesterday);
        }
      : undefined;

  return (
    <div className="flex w-full items-center md:gap-2">
      {withArrows && (
        <ArrowsGroup
          className="hover:shadow-none"
          leftProps={{
            onClick: handlePrev,
            className: "h-[42px]"
          }}
          showLeft={!isMd}
          showRight={false}
        />
      )}
      <Popover
        mainContainerClassName={""}
        content={
          <>
            {calendar}
            {showMultiDaySwitch && (
              <div className={"mt-2 select-none p-2.5"}>
                <Switch
                  variant={"squared"}
                  size={"xs"}
                  label={"Multi-Day"}
                  onCheckedChange={handleMultiDaySwitch}
                  checked={forceMultiDay}
                />
              </div>
            )}
          </>
        }
        footer={
          setValueOnApply ? (
            <ModalButtons
              onApply={handleOnApply}
              onCancel={hideCancel ? undefined : handleCancel}
            />
          ) : undefined
        }
        sideContent={
          mode === "multiple" && !hideSideContent ? (
            <CalendarDatesList
              dates={Array.isArray(selectedDates) ? selectedDates : []}
              onDelete={deleteDate}
            />
          ) : undefined
        }
        onOpenChange={pickerDisclosure.onOpenChange}
        open={pickerDisclosure.isOpen}
      >
        <CalendarTrigger
          variant={variant}
          dates={selectedDate}
          icon={Calendar2Icon}
          showDaysCount={showDaysCount}
          showDayName={showDayName}
          hideMultipleText={hideMultipleText}
          className={cn(
            withArrows && "rounded-none",
            "w-full flex-1 text-[12px] md:rounded-lg md:text-[14px]",
            inputClassName
          )}
        />
      </Popover>
      {withArrows && (
        <ArrowsGroup
          className="hover:shadow-none"
          rightProps={{
            onClick: handleNext,
            className: "h-[42px]"
          }}
          leftProps={{
            onClick: handlePrev,
            className: "h-[42px]"
          }}
          showLeft={isMd}
        />
      )}
    </div>
  );
});

CalendarInputInput.displayName = "CalendarInputInput";

export const CalendarInput = React.forwardRef<
  HTMLInputElement,
  CalendarInputType
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <>
      <FormControlWrapper
        variant={"unstyled"}
        {...wrapperProps}
        wrapperClassName={cn("py-0", props.wrapperClassName)}
      >
        <CalendarInputInput
          ref={ref}
          {...inputProps}
          required={false}
          withArrows={props.withArrows}
          showMultipleLabel={props.showMultipleLabel}
          showDayName={props.showDayName}
          hideSideContent={props.hideSideContent}
          hideCancel={props.hideCancel}
          hideMultipleText={props.hideMultipleText}
          variant={props.variant}
        />
      </FormControlWrapper>
    </>
  );
});

CalendarInput.displayName = "CalendarInput";

export type { CalendarInputType };
